import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useAppSelector } from 'states/hooks'
import {
  ActiveChatRoomInfo,
  useLoadUserSettings,
  useSelectSortedAlreadyReadFavoriteChatActiveChatRooms,
  useUserSetting,
} from '@crew/states'
import { FavoriteDisplayOrderTypeValues, SettingKeyType } from '@crew/enums/app'
import { FavoriteDisplayOrderType, ProjectType } from '@crew/enums/domain'
import { getEnumDataByValue } from '@crew/utils/enum'
import { useShowApiErrors } from 'hooks/useShowApiErrors'
import { useDispatch } from 'react-redux'
import {
  ObjectEventMessage,
  notifyFavoriteEvent,
} from 'features/app/states/appSlice'
import { Favorite } from '@crew/models/domain'
import { NotifyEventType } from 'enums/app'
import { arrayMove } from '@dnd-kit/sortable'
import { CrewSortableUnorderedList } from 'components/elements/crewSortableUnorderedList/crewSortableUnorderedList'
import { useFavoriteChatList } from './usefavoriteChatList'
import { FavoriteChatListDirectChatItem } from './components/favoriteChatListDirectChatItem/favoriteChatListDirectChatItem'
import { FavoriteChatListGroupChatItem } from './components/favoriteChatListGroupChatItem/favoriteChatListGroupChatItem'

// お気に入りチャットリスト内のアイテムレンダラー
// ドラッグ中も同様のアイテムを表示する
const chatItem = (item: ActiveChatRoomInfo & { id: string }) => {
  // ダイレクトチャットまたはグループチャット以外は表示しない
  if (
    item.projectType !== ProjectType.DirectChannel &&
    item.projectType !== ProjectType.InstantChannel
  ) {
    return null
  }

  return (
    <>
      {item.projectType === ProjectType.DirectChannel && (
        <FavoriteChatListDirectChatItem key={item.chatRoomId} chatRoom={item} />
      )}
      {item.projectType === ProjectType.InstantChannel && (
        <FavoriteChatListGroupChatItem key={item.chatRoomId} chatRoom={item} />
      )}
    </>
  )
}

// 並び替え可能なお気に入りチャットリスト内のアイテムレンダラー
const favoriteChatListRenderItem = (
  item: ActiveChatRoomInfo & { id: string }
) => {
  // ダイレクトチャットまたはグループチャット以外は表示しない
  if (
    item.projectType !== ProjectType.DirectChannel &&
    item.projectType !== ProjectType.InstantChannel
  ) {
    return null
  }

  return (
    <CrewSortableUnorderedList.Item id={item.id}>
      {chatItem(item)}
    </CrewSortableUnorderedList.Item>
  )
}

// アコーディオン内に表示されるアイテム表示
export const FavoriteChatList: FC = memo(() => {
  const dispatch = useDispatch()
  const [showApiErrors] = useShowApiErrors()
  const [loadUserSettings] = useLoadUserSettings()
  const { reorderFavoriteProject } = useFavoriteChatList()

  // ローカル状態での並び替え管理
  const [localChatRooms, setLocalChatRooms] = useState<
    (ActiveChatRoomInfo & { id: string })[]
  >([])

  // 左メニューの表示更新を通知する
  const loadActiveChatRooms = useCallback(() => {
    const objectEventMessage: ObjectEventMessage<Favorite> = {
      eventType: NotifyEventType.Updated,
      id: '',
      object: undefined,
    }
    dispatch(notifyFavoriteEvent(objectEventMessage))
  }, [dispatch])

  // 個人設定のお気に入り表示順設定
  const favoriteDisplayOrderType = useUserSetting(
    SettingKeyType.FavoriteDisplayOrderType,
    FavoriteDisplayOrderType.Activity
  )

  // 表示順に応じてソートした、未読のないお気に入りチャット（ダイレクトチャット＋グループチャット)のアクティブチャットルームを取得する
  const selectSortedAlreadyReadFavoriteChatActiveChatRooms =
    useSelectSortedAlreadyReadFavoriteChatActiveChatRooms()
  const sortedAlreadyReadDirectChannelChatRooms = useAppSelector((state) => {
    // お気に入り表示順が取得できていない場合は何も表示しない
    if (!favoriteDisplayOrderType) {
      return []
    }
    const sortType = getEnumDataByValue(
      FavoriteDisplayOrderTypeValues,
      favoriteDisplayOrderType
    )
    // 通常はありえないが、お気に入り表示順の値がenumに存在しない場合は何も表示しない
    if (!sortType) {
      return []
    }
    return selectSortedAlreadyReadFavoriteChatActiveChatRooms(
      state,
      sortType.value
    )
  })

  // メモ化してidプロパティを追加
  const chatRoomsWithId = useMemo(() => {
    return sortedAlreadyReadDirectChannelChatRooms.map((chatRoom) => ({
      ...chatRoom,
      id: chatRoom.projectId,
    }))
  }, [sortedAlreadyReadDirectChannelChatRooms])

  // セレクターの結果をローカル状態に同期
  useEffect(() => {
    setLocalChatRooms(chatRoomsWithId)
  }, [chatRoomsWithId])

  // 並び替え処理
  const handleReorder = useCallback(
    async (fromId: string, toId: string) => {
      setLocalChatRooms((items) => {
        if (!items) {
          return items
        }
        const from = items.findIndex((item) => item.id === fromId)
        const to = items.findIndex((item) => item.id === toId)
        return arrayMove(items, from, to)
      })

      try {
        // APIを呼び出し
        await reorderFavoriteProject(fromId, toId)
        // 個人設定を再読み込み
        loadUserSettings()
        // お気に入り状態が変更されたので、左メニューの表示を更新する
        loadActiveChatRooms()
      } catch (error) {
        showApiErrors(error)
      }
    },
    [
      reorderFavoriteProject,
      loadUserSettings,
      loadActiveChatRooms,
      showApiErrors,
    ]
  )

  return (
    <>
      {localChatRooms.length > 0 ? (
        <CrewSortableUnorderedList
          items={localChatRooms}
          onReorder={handleReorder}
          className="flex-1"
          renderItem={favoriteChatListRenderItem}
          renderOverlayItem={chatItem}
        />
      ) : (
        <ul className="flex-1" />
      )}
    </>
  )
})
